import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import adminAllStoresService from '../../../services/adminAllStoresService';
import type { AdminAllStoresResponse } from '../../../types/features/adminAllStores';
import type { StoreSummarySortKey, SubscriptionStatus } from '../../../types/features/storeSummaryListing';
import { transformStoresSummary, type ProcessedStoreRow } from '../../../utils/storeSummaryTransform';

const DEFAULT_STATUS_FILTER: SubscriptionStatus = 'active';

const parsePositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number.parseInt(value || '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const useStoreSummaryListing = () => {
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialStatus = (searchParams.get('status') as SubscriptionStatus) || DEFAULT_STATUS_FILTER;
  const initialSearch = searchParams.get('search') || '';
  const initialPage = parsePositiveInt(searchParams.get('page'), 1);
  const initialPageSize = parsePositiveInt(searchParams.get('page_size'), 25);

  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus>(initialStatus);
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [orderBy, setOrderBy] = useState<StoreSummarySortKey | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<AdminAllStoresResponse | null>(null);
  const debouncedSearch = useDebouncedValue(search, 1000);

  const isSuperAdmin = useMemo(() => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    return Boolean(userDetails?.is_super_admin);
  }, []);

  const processedRows = useMemo(() => transformStoresSummary(data), [data]);
  const filteredRows = useMemo(() => {
    const searchTerm = (debouncedSearch || '').toString().trim().toLowerCase();

    return processedRows.filter((row) => {
      const searchableValues = [
        row.name,
        row.raw.store_key,
        row.ownerName,
        row.ownerPhone,
        row.raw.city,
        row.raw.state,
        row.raw.postal_code,
        row.raw.country,
        row.raw.brand,
        row.raw.pos_type,
        row.raw.lottery_reseller_id,
        row.raw.lotto_reco_console_id,
        row.id,
      ];
      const matchesSearch = !searchTerm || searchableValues.some((value) =>
        String(value || '').toLowerCase().includes(searchTerm)
      );

      return matchesSearch;
    });
  }, [debouncedSearch, processedRows]);

  const sortedRows = useMemo(() => {
    if (!orderBy) {
      return filteredRows;
    }

    const sortableRows = [...filteredRows];
    const getSortValue = (row: ProcessedStoreRow) => {
      switch (orderBy) {
        case 'name':
          return row.name.toLowerCase();
        case 'owner':
          return row.ownerName.toLowerCase();
        case 'registrationDate':
          return new Date(row.registrationDate).getTime();
        case 'remainingDays':
          return typeof row.remainingDays === 'number' ? row.remainingDays : Number.NEGATIVE_INFINITY;
        default:
          return 0;
      }
    };

    sortableRows.sort((left, right) => {
      const leftValue = getSortValue(left);
      const rightValue = getSortValue(right);

      if (leftValue < rightValue) {
        return order === 'asc' ? -1 : 1;
      }

      if (leftValue > rightValue) {
        return order === 'asc' ? 1 : -1;
      }

      return 0;
    });

    return sortableRows;
  }, [filteredRows, order, orderBy]);

  const rows = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return sortedRows.slice(startIndex, startIndex + pageSize);
  }, [page, pageSize, sortedRows]);

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');

    try {
      const response = await adminAllStoresService.getAllStores({
        subscription_status: statusFilter,
      });

      if (requestId === requestIdRef.current) {
        setData(response);
      }
    } catch (requestError: any) {
      if (requestId === requestIdRef.current) {
        setError(requestError?.message || 'Failed to load store summary');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [statusFilter]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    params.set('page_size', String(pageSize));

    if (statusFilter !== 'all') {
      params.set('status', statusFilter);
    } else {
      params.delete('status');
    }

    const searchTerm = (debouncedSearch || '').toString().trim();
    if (searchTerm) {
      params.set('search', searchTerm);
    } else {
      params.delete('search');
    }

    setSearchParams(params, { replace: true });
  }, [debouncedSearch, page, pageSize, searchParams, setSearchParams, statusFilter]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchData();
    }
  }, [fetchData, isSuperAdmin]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, pageSize, sortedRows.length]);

  const handleStatusChange = useCallback((value: SubscriptionStatus) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleClearAll = useCallback(() => {
    setSearch('');
    setStatusFilter(DEFAULT_STATUS_FILTER);
    setPage(1);
  }, []);

  const handleRequestSort = useCallback((key: StoreSummarySortKey) => {
    setPage(1);
    setOrder((previousOrder) => (orderBy === key && previousOrder === 'asc' ? 'desc' : 'asc'));
    setOrderBy((previousKey) => (previousKey === key ? previousKey : key));
  }, [orderBy]);

  const handleRowsPerPageChange = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
  }, []);

  const handleRowClick = useCallback((storeId: string) => {
    navigate(`/dashboard/store-summary/stores/${storeId}${window.location.search}`);
  }, [navigate]);

  const handleBackToScratchers = useCallback(() => {
    navigate('/dashboard/store-summary');
  }, [navigate]);

  return {
    model: {
      isSuperAdmin,
      loading,
      error,
      statusFilter,
      search,
      rows,
      total: filteredRows.length,
      page,
      pageSize,
      orderBy,
      order,
    },
    actions: {
      onStatusChange: handleStatusChange,
      onSearchChange: handleSearchChange,
      onClearAll: handleClearAll,
      onRequestSort: handleRequestSort,
      onPageChange: setPage,
      onRowsPerPageChange: handleRowsPerPageChange,
      onRowClick: handleRowClick,
      onBackToScratchers: handleBackToScratchers,
    },
  };
};
